import { TaskFlowDto, TaskFlowSchemaDto } from '@shared/services/apiService/apiService';
import {
  TaskEditorValue,
  TaskFlowDtmfStopEvent,
  TaskFlowTaskDto,
  TaskFlowTaskType,
  TaskGroupEditorValue,
} from '../domain/contracts';
import { normalizeTask } from '../domain/taskTypeRegistry';

export type TaskFlowCanvasNode = {
  id: string;
  position: { x: number; y: number };
  data: { task: TaskEditorValue; index: number };
};

export type TaskFlowCanvasEdge = {
  id: string;
  source: string;
  target: string;
};

const X_SPACING = 260;
const Y = 120;
const X_START = 80;

export const buildTaskFlowCanvasGraph = (
  tasks: TaskEditorValue[],
): { nodes: TaskFlowCanvasNode[]; edges: TaskFlowCanvasEdge[] } => {
  const nodes = tasks.map((task, index) => ({
    id: task.clientKey,
    position: { x: X_START + index * X_SPACING, y: Y },
    data: { task, index },
  }));

  const edges = tasks.slice(1).map((task, index) => ({
    id: `edge-${tasks[index].clientKey}-${task.clientKey}`,
    source: tasks[index].clientKey,
    target: task.clientKey,
  }));

  return { nodes, edges };
};

export const normalizeTaskGroup = (
  input: TaskFlowDto | null | undefined,
): TaskGroupEditorValue | null => {
  if (!input) return null;
  return {
    id: input.id,
    ...(input.autoStart === undefined ? {} : { autoStart: input.autoStart }),
    ...(input.summarizeChatCtx === undefined ? {} : { summarizeChatCtx: input.summarizeChatCtx }),
    ...(input.returnExceptions === undefined ? {} : { returnExceptions: input.returnExceptions }),
    tasks: input.tasks.map(normalizeTask),
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const serializeSchema = (schema: unknown): TaskFlowSchemaDto | undefined => {
  if (!isRecord(schema) || !isRecord(schema.properties)) return undefined;

  const properties: TaskFlowSchemaDto['properties'] = {};
  for (const [name, rawProperty] of Object.entries(schema.properties)) {
    if (!isRecord(rawProperty) || typeof rawProperty.type !== 'string') continue;
    properties[name] = {
      type: rawProperty.type as TaskFlowSchemaDto['properties'][string]['type'],
      ...(typeof rawProperty.description === 'string'
        ? { description: rawProperty.description }
        : {}),
    };
  }

  const required = Array.isArray(schema.required)
    ? schema.required.filter((value): value is string => typeof value === 'string')
    : undefined;

  return { properties, ...(required === undefined ? {} : { required }) };
};

export const sanitizeTaskForType = (task: TaskEditorValue): TaskEditorValue => {
  if (task.type === 'collect_data') return task;

  const nextTask = { ...task };
  delete nextTask.schema;
  delete nextTask.schemaType;
  return nextTask;
};

export const serializeTask = (task: TaskEditorValue): TaskFlowTaskDto => {
  const safeTask = sanitizeTaskForType(task);
  const serialized = {
    id: safeTask.id,
    type: safeTask.type,
    description: safeTask.description,
  } as {
    id: string;
    type: TaskFlowTaskType;
    description: string;
    instructions?: string | null;
    schema?: TaskFlowSchemaDto | null;
    schemaType?: 'json_schema';
    numDigits?: number;
    dtmfInputTimeout?: number;
    dtmfStopEvent?: TaskFlowDtmfStopEvent;
    askForConfirmation?: boolean;
  };

  if (safeTask.instructions === null) {
    serialized.instructions = null;
  } else if (typeof safeTask.instructions === 'string' && safeTask.instructions.trim()) {
    serialized.instructions = safeTask.instructions;
  }

  if (safeTask.type === 'collect_data') {
    const schema = serializeSchema(safeTask.schema);
    if (schema) {
      serialized.schema = schema;
      if (safeTask.schemaType === 'json_schema') serialized.schemaType = 'json_schema';
    }
  }

  if (safeTask.type === 'get_dtmf') {
    if (safeTask.numDigits !== undefined && safeTask.numDigits !== null) {
      serialized.numDigits = safeTask.numDigits;
    }
    if (safeTask.dtmfInputTimeout !== undefined && safeTask.dtmfInputTimeout !== null) {
      serialized.dtmfInputTimeout = safeTask.dtmfInputTimeout;
    }
    if (typeof safeTask.dtmfStopEvent === 'string' && safeTask.dtmfStopEvent) {
      serialized.dtmfStopEvent = safeTask.dtmfStopEvent as TaskFlowDtmfStopEvent;
    }
    if (safeTask.askForConfirmation !== undefined) {
      serialized.askForConfirmation = safeTask.askForConfirmation;
    }
  }

  return serialized as TaskFlowTaskDto;
};

export const serializeTaskGroup = (group: TaskGroupEditorValue): TaskFlowDto | null => {
  if (group.tasks.length === 0) return null;
  return {
    id: group.id,
    ...(group.autoStart === undefined ? {} : { autoStart: group.autoStart }),
    ...(group.summarizeChatCtx === undefined ? {} : { summarizeChatCtx: group.summarizeChatCtx }),
    ...(group.returnExceptions === undefined ? {} : { returnExceptions: group.returnExceptions }),
    tasks: group.tasks.map(serializeTask),
  };
};

export const reorderTasks = (
  tasks: TaskEditorValue[],
  fromIndex: number,
  toIndex: number,
): TaskEditorValue[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= tasks.length) {
    return tasks;
  }
  const next = [...tasks];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(Math.min(toIndex, next.length), 0, moved);
  return next;
};
